import request from '@teetsh/app/src/utils/request';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import useNotification from 'containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';
import { buildPyramideParams } from 'containers/PyramidesDesAges/Pyramide/buildPyramideParams';
import { Pyramide, PyramidePDFSettings } from 'types/pyramide';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';

interface Props {
  schoolyearId: string;
  schoolId: string;
  selectedGroups?: string[];
  onStart: () => void;
  onFinished: () => void;
  onError: () => void;
  settings: PyramidePDFSettings;
  lng: string;
  pyramide: Pyramide;
}

function fetchPyramideAPI(s3Url: string, pyramideName: string) {
  return fetch(s3Url)
    .then(response => response.blob())
    .then(response => {
      saveAs(response, `${pyramideName}.pdf`);
    });
}

export async function downloadPyramide(props: Props) {
  const { schoolyearId, schoolId, onStart, onFinished, onError, pyramide } =
    props;

  onStart();

  const params = buildPyramideParams({
    lng: props.lng,
    groups: props.selectedGroups,
    settings: props.settings,
    pyramide: props.pyramide,
  });

  const requestURL = `/schoolyear/${schoolyearId}/school/${schoolId}/pyramides/${
    pyramide.id
  }/generate?${params.toString()}`;
  try {
    const response = await request(requestURL, {
      method: 'GET',
    });
    const token = response.pdf_token;
    await fetchPyramideAPI(token, pyramide.title);
  } catch {
    onError();
  } finally {
    onFinished();
  }
}

export default function useDownloadPyramide() {
  const user = useUser();
  const { schoolyear, school } = useSchoolSchoolyear();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { notifyError } = useNotification();
  const { t } = useTranslation(['common']);

  const download = ({
    settings,
    pyramide,
    selectedGroups,
  }: {
    settings: PyramidePDFSettings;
    pyramide: Pyramide;
    selectedGroups?: string[];
  }) =>
    downloadPyramide({
      lng: user.locale,
      pyramide,
      settings,
      selectedGroups,
      schoolyearId: schoolyear.id,
      schoolId: school.id,
      onStart: () => setIsDownloading(true),
      onFinished: () => setIsDownloading(false),
      onError: () => notifyError({ title: t('common:messages.genericError') }),
    });

  return {
    download,
    isDownloading,
  };
}
